// src/components/onboarding/Walkthrough.jsx
//
// First-run walkthrough overlay — 5 steps that navigate through key screens.
// Each step renders a compact tooltip card with a directional arrow pointing
// at the feature being described.

import { useEffect, useState } from "react";
import { T } from "../../theme.js";

export const WALKTHROUGH_KEY = "orbit_walkthrough_done";

// ─── Step definitions ─────────────────────────────────────────────────────────
// spotlight.type:
//   "nav"      → pulsing ring over a bottom-nav tab (tabIndex 0–4, space-around → 10/30/50/70/90%)
//   "gear"     → pulsing ring over the top-right settings gear
//   "add-btn"  → pulsing ring over the + Add button in the People header
//   "logo"     → pulsing ring over the Orbit logo (top-left)
//
// card.anchor:
//   "bottom-nav"   → card above nav bar; arrow ↓ at nav tab (alignPct = tab center %)
//   "top-right"    → card below gear icon; arrow ↑ aligned right
//   "top-left"     → card below logo; arrow ↑ aligned left
//   "add-btn"      → card below + Add button (top-right of People header); arrow ↑ right
//   "center"       → card centered; no arrow

const STEPS = [
  {
    id: "welcome",
    screen: null,
    emoji: "◎",
    emojiGradient: true,
    title: "Welcome to Orbit",
    body: "Orbit keeps you close to the people who matter most. Here's a quick tour.",
    spotlight: { type: "logo" },
    card: { anchor: "top-left" },
    cta: "Let's go →",
  },
  {
    id: "people",
    screen: "people",
    emoji: "👥",
    title: "Add your people",
    body: "Tap + Add to bring your partner, family, and friends into your orbit.",
    spotlight: { type: "add-btn" },
    card: { anchor: "add-btn" },
    cta: "Next →",
  },
  {
    id: "calendar",
    screen: "calendars",
    emoji: "📅",
    title: "Connect your calendar",
    body: "Link Google, Outlook, or iCloud so Orbit knows when you're free — without sharing the details.",
    spotlight: { type: "nav", tabIndex: 3 },
    card: { anchor: "bottom-nav", alignPct: 70 },
    cta: "Next →",
  },
  {
    id: "schedule",
    screen: "schedule",
    emoji: "📆",
    title: "Plan time together",
    body: "Propose times, see overlapping availability, and agree on a plan — no back-and-forth texts.",
    spotlight: { type: "nav", tabIndex: 2 },
    card: { anchor: "bottom-nav", alignPct: 50 },
    cta: "Next →",
  },
  {
    id: "done",
    screen: "home",
    emoji: "✨",
    title: "You're all set!",
    body: "Your home screen updates live as your calendar and connections change.",
    spotlight: { type: "logo" },
    card: { anchor: "top-left" },
    cta: "Get started",
  },
];

// Nav tab centers: 5 tabs with justify-content:space-around → 10%, 30%, 50%, 70%, 90%
function navTabCenter(tabIndex) {
  return (2 * tabIndex + 1) * 10;
}

const CARD_W = 260;
const ARROW_SIZE = 10;

// ─── Component ────────────────────────────────────────────────────────────────
// Desktop sidebar: 210px wide. Nav items start after ~78px header, each ~40px tall.
// tabIndex 0=Home,1=People,2=Schedule,3=Calendars,4=Inbox
function desktopNavItemCenter(tabIndex) {
  return 78 + tabIndex * 40 + 20; // px from top of viewport
}

export function Walkthrough({ onNav, onDone, isMobile = true }) {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  useEffect(() => {
    if (current.screen) onNav(current.screen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const advance = () => { if (isLast) finish(); else setStep(s => s + 1); };
  const back    = () => setStep(s => s - 1);
  const finish  = () => {
    try { localStorage.setItem(WALKTHROUGH_KEY, "1"); } catch {}
    onNav("home");
    onDone();
  };

  const { spotlight, card } = current;

  // ── Card positioning ─────────────────────────────────────────────────────────
  const { posStyle, arrowStyle } = (() => {
    const { anchor, alignPct } = card;

    if (anchor === "bottom-nav") {
      if (isMobile) {
        // Mobile: card above the bottom nav bar; arrow ↓ at the tab
        return {
          posStyle: {
            position: "fixed",
            bottom: `calc(max(18px, env(safe-area-inset-bottom, 18px)) + 74px)`,
            left: `clamp(12px, calc(${alignPct}% - ${CARD_W / 2}px), calc(100vw - ${CARD_W + 12}px))`,
            width: CARD_W,
          },
          arrowStyle: {
            position: "fixed",
            bottom: `calc(max(18px, env(safe-area-inset-bottom, 18px)) + 64px)`,
            left: `calc(${alignPct}% - ${ARROW_SIZE}px)`,
            width: 0, height: 0,
            borderLeft: `${ARROW_SIZE}px solid transparent`,
            borderRight: `${ARROW_SIZE}px solid transparent`,
            borderTop: `${ARROW_SIZE}px solid ${T.surface}`,
            zIndex: 10000, pointerEvents: "none",
          },
        };
      } else {
        // Desktop: card to the right of the sidebar nav item; arrow ← points left at the item
        const tabIndex = current.spotlight?.tabIndex ?? 0;
        const cy = desktopNavItemCenter(tabIndex);
        return {
          posStyle: {
            position: "fixed",
            top: Math.max(12, cy - 80),
            left: 222, // just right of the 210px sidebar
            width: CARD_W,
          },
          arrowStyle: {
            position: "fixed",
            top: cy - ARROW_SIZE,
            left: 210,
            width: 0, height: 0,
            borderTop: `${ARROW_SIZE}px solid transparent`,
            borderBottom: `${ARROW_SIZE}px solid transparent`,
            borderRight: `${ARROW_SIZE}px solid ${T.surface}`,
            zIndex: 10000, pointerEvents: "none",
          },
        };
      }
    }

    if (anchor === "top-right") {
      // Card below the gear icon; arrow ↑ aligned with gear center (~28px from right)
      return {
        posStyle: {
          position: "fixed",
          top: `calc(env(safe-area-inset-top, 0px) + 52px)`,
          right: 10,
          width: CARD_W,
        },
        arrowStyle: {
          position: "fixed",
          top: `calc(env(safe-area-inset-top, 0px) + 42px)`,
          right: 26,          // aligns with gear center (~28px from right edge)
          width: 0, height: 0,
          borderLeft: `${ARROW_SIZE}px solid transparent`,
          borderRight: `${ARROW_SIZE}px solid transparent`,
          borderBottom: `${ARROW_SIZE}px solid ${T.surface}`,
          zIndex: 10000, pointerEvents: "none",
        },
      };
    }

    if (anchor === "top-left") {
      if (isMobile) {
        // Mobile: card below the Orbit logo in top bar
        return {
          posStyle: {
            position: "fixed",
            top: `calc(env(safe-area-inset-top, 0px) + 52px)`,
            left: 10,
            width: CARD_W,
          },
          arrowStyle: {
            position: "fixed",
            top: `calc(env(safe-area-inset-top, 0px) + 42px)`,
            left: 26,
            width: 0, height: 0,
            borderLeft: `${ARROW_SIZE}px solid transparent`,
            borderRight: `${ARROW_SIZE}px solid transparent`,
            borderBottom: `${ARROW_SIZE}px solid ${T.surface}`,
            zIndex: 10000, pointerEvents: "none",
          },
        };
      } else {
        // Desktop: card to the right of sidebar logo
        return {
          posStyle: {
            position: "fixed",
            top: 12,
            left: 222,
            width: CARD_W,
          },
          arrowStyle: {
            position: "fixed",
            top: 28,
            left: 210,
            width: 0, height: 0,
            borderTop: `${ARROW_SIZE}px solid transparent`,
            borderBottom: `${ARROW_SIZE}px solid transparent`,
            borderRight: `${ARROW_SIZE}px solid ${T.surface}`,
            zIndex: 10000, pointerEvents: "none",
          },
        };
      }
    }

    if (anchor === "add-btn") {
      // Card below the + Add button (top-right); arrow ↑ aligned with button center (~45px from right)
      return {
        posStyle: {
          position: "fixed",
          top: `calc(env(safe-area-inset-top, 0px) + 144px)`,
          right: 10,
          width: CARD_W,
        },
        arrowStyle: {
          position: "fixed",
          top: `calc(env(safe-area-inset-top, 0px) + 134px)`,
          right: 44,          // + Add button center is ~45px from right (14px margin + ~31px into button)
          width: 0, height: 0,
          borderLeft: `${ARROW_SIZE}px solid transparent`,
          borderRight: `${ARROW_SIZE}px solid transparent`,
          borderBottom: `${ARROW_SIZE}px solid ${T.surface}`,
          zIndex: 10000, pointerEvents: "none",
        },
      };
    }

    // center — no arrow
    return {
      posStyle: {
        position: "fixed",
        left: "50%", top: "50%",
        transform: "translate(-50%, -50%)",
        width: CARD_W,
      },
      arrowStyle: null,
    };
  })();

  return (
    <>
      <style>{`
        @keyframes wt-card-up {
          from { opacity:0; transform:translateY(10px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes wt-card-center {
          from { opacity:0; transform:translate(-50%,-48%); }
          to   { opacity:1; transform:translate(-50%,-50%); }
        }
        @keyframes wt-fade {
          from { opacity:0; }
          to   { opacity:1; }
        }
      `}</style>

      {/* Full-screen dimmer / click-to-advance */}
      <div
        onClick={advance}
        style={{
          position: "fixed", inset: 0, zIndex: 9997,
          background: "rgba(0,0,0,0.55)",
          animation: "wt-fade .3s ease",
        }}
      />


      {/* Arrow (fixed, so it's always between spotlight and card) */}
      {arrowStyle && <div style={arrowStyle} />}

      {/* Tooltip card */}
      <div
        key={step}
        onClick={e => e.stopPropagation()}
        style={{
          ...posStyle,
          zIndex: 9999,
          background: T.surface,
          borderRadius: 16,
          padding: "16px 16px 14px",
          boxShadow: "0 6px 32px rgba(30,16,8,0.26)",
          border: `1px solid ${T.edge}`,
          animation: card.anchor === "center" ? "wt-card-center .28s ease" : "wt-card-up .28s ease",
        }}
      >
        {/* Step dots + emoji on same row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          {current.emojiGradient
            ? <img src="/orbit-logo.png" alt="Orbit" style={{width:36,height:36,objectFit:"contain",flexShrink:0}}/>
            : <div style={{
                width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                background: T.clay + "18",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20,
              }}>
                {current.emoji}
              </div>
          }
          <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
            {STEPS.map((_, i) => (
              <div key={i} style={{
                width: i === step ? 16 : 5, height: 5, borderRadius: 3,
                background: i === step ? T.clay : T.edgeMid,
                transition: "width .25s, background .25s",
              }} />
            ))}
          </div>
        </div>

        {/* Text */}
        <h3 style={{
          fontSize: 16, fontFamily: "'Crimson Pro', serif",
          fontWeight: 600, color: T.ink, marginBottom: 5, lineHeight: 1.2,
        }}>
          {current.title}
        </h3>
        <p style={{ fontSize: 12, color: T.bark, lineHeight: 1.55, marginBottom: 14 }}>
          {current.body}
        </p>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {step > 0 && (
            <button onClick={back} style={{
              width: 30, height: 30, borderRadius: "50%",
              background: T.mist, border: `1px solid ${T.edge}`,
              fontSize: 12, color: T.muted, cursor: "pointer", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>←</button>
          )}
          {!isLast && (
            <button onClick={finish} style={{
              background: "none", border: "none",
              fontSize: 11, color: T.muted, cursor: "pointer", padding: "6px 2px", flexShrink: 0,
            }}>Skip</button>
          )}
          <button onClick={advance} style={{
            flex: 1, padding: "9px",
            borderRadius: 11,
            background: isLast ? `linear-gradient(135deg, ${T.bark}, ${T.clay})` : T.bark,
            border: "none", color: "white",
            fontSize: 13, fontWeight: 600, cursor: "pointer",
            boxShadow: `0 3px 12px ${T.bark}33`,
          }}>
            {current.cta}
          </button>
        </div>
      </div>
    </>
  );
}
